import { FC, useState, useEffect } from "react";
import { observer } from "mobx-react";
import { useLocation, useParams } from "react-router";

import ChatContent from "../components/chat/ChatContent";
import Row from "../components/ui/core/Row";
import DropZoneWrapper from "../components/ui/DropZoneWrapper";
import { DEFAULT_FILE_ACCEPT } from "../config/channels";
import { useChatStore } from "../hooks/useChatStore";
import ChatPanel from "../components/chat/components/ChatPanel";
import { useAppStore } from "../hooks/useAppStore";
import { useCapabilities } from "../contexts/permissions/useCapabilities";
import { useAuth } from "../contexts/AuthContext";

const ChatPage: FC = observer(() => {
  const chatStore = useChatStore();
  const location = useLocation()
  const { channelId } = useParams<{ channelId: string }>();
  const { sidebarWidth } = useAppStore();
  const { is_external } = useAuth()
  const { canUploadAttachments, showSidebar } = useCapabilities();

  const [panelPosition, setPanelPosition] = useState<"left" | "right">("right");

  // Sync URL to ChatStore so activeChatId is set when navigating to /channels/:channelId (e.g. refresh, browser back)
  // "new" is a virtual channel for draft chats (e.g. "Ask Reve") — keep activeChatId null so
  // ChatContent renders the new-chat view and the chat is only created on first message send.
  useEffect(() => {
    if (channelId && channelId !== 'new' && channelId !== chatStore.activeChatId) {
      chatStore.setActiveChatId(channelId);
    }
  }, [channelId]);

  const availableWidth = (location.pathname === "/dashboard") || (is_external && !showSidebar) ? "100%" : `calc(100vw - ${sidebarWidth}px)`;

  return (
    <main id="chat-container-ref" className="grow flex flex-row bg-hex-gray-100 h-[calc(100%-6rem)]" style={{ width: availableWidth }}>
      <DropZoneWrapper
        className="h-full min-h-0 min-w-0 overflow-hidden flex flex-col items-center w-full"
        onFilesDropped={(files) => chatStore.setDroppedFiles(files as any)}
        disabled={!canUploadAttachments || chatStore.isInputDisabled || (!chatStore.isChatLoading && !chatStore.chatDetails?.id)}
        acceptTypes={DEFAULT_FILE_ACCEPT}
      >
        <Row
          sx={{
            flexDirection: panelPosition === "left" ? "row-reverse" : "row",
          }}
        >
          <ChatContent />

          <ChatPanel
            panelPosition={panelPosition}
            setPanelPosition={()=> setPanelPosition(prev => prev === "left" ? "right" : "left")}
          />
        </Row>
      </DropZoneWrapper>
    </main>
  );
});

export default ChatPage;
